/**
 * =============================================================================
 * JOB WORKSPACE INLINE DRAWERS & PANELS
 * =============================================================================
 * 
 * PHASE 2A: Job Workspace Maturity
 * 
 * These components enable operators to complete workflows WITHOUT leaving
 * the Job Workspace:
 * 
 * 1. QuoteReviewDrawer - View/approve/reject quotes inline
 * 2. InvoiceReviewDrawer - View/approve/query invoices inline  
 * 3. EvidenceUploadPanel - Drag-drop file uploads with categorization
 * 4. SLAStatusStrip - Instant operational awareness at top
 * 5. ActivityFeed - Unified chronological timeline
 */

import React, { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  X, Check, AlertTriangle, FileText, DollarSign, 
  Upload, Camera, File, Clock, User, Wrench,
  TrendingUp, TrendingDown, Minus, Download, Eye,
  MessageSquare, Calendar, Shield, Receipt, Edit,
  AlertCircle, CheckCircle, XCircle, ChevronRight,
  Loader2, Image, Paperclip, Plus, ArrowRight,
  History, Zap, Phone, Mail, Building2
} from 'lucide-react';
import { TOKENS } from './PHSCommandLayout';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// SLA STATUS STRIP - Instant operational awareness
// =============================================================================
export function SLAStatusStrip({ job }) {
  const getSLAStatus = () => {
    if (!job.sla_due_at) return { status: 'No SLA', color: '#6B7280', bgColor: '#F3F4F6', urgency: 0 };
    
    const now = new Date();
    const due = new Date(job.sla_due_at);
    const hoursRemaining = (due - now) / (1000 * 60 * 60);
    
    if (hoursRemaining < 0) {
      const hoursOverdue = Math.abs(hoursRemaining);
      return { 
        status: 'BREACHED', 
        detail: `${Math.ceil(hoursOverdue)}h overdue`,
        color: '#DC2626', 
        bgColor: '#FEE2E2',
        urgency: 4
      };
    } else if (hoursRemaining <= 4) {
      return { 
        status: 'CRITICAL', 
        detail: `${Math.ceil(hoursRemaining)}h remaining`,
        color: '#DC2626', 
        bgColor: '#FEE2E2',
        urgency: 3
      };
    } else if (hoursRemaining <= 24) {
      return { 
        status: 'AT RISK', 
        detail: `${Math.ceil(hoursRemaining)}h remaining`,
        color: '#F59E0B', 
        bgColor: '#FEF3C7',
        urgency: 2
      };
    } else if (hoursRemaining <= 48) {
      return { 
        status: 'WARNING', 
        detail: `${Math.round(hoursRemaining / 24)}d remaining`,
        color: '#F59E0B', 
        bgColor: '#FEF3C7',
        urgency: 1
      };
    }
    return { 
      status: 'HEALTHY', 
      detail: `${Math.round(hoursRemaining / 24)}d remaining`,
      color: '#22C55E', 
      bgColor: '#DCFCE7',
      urgency: 0
    };
  };

  const sla = getSLAStatus();
  const priority = job.priority?.toLowerCase() || 'medium';
  const priorityColors = {
    emergency: { color: '#DC2626', bg: '#FEE2E2' },
    high: { color: '#F59E0B', bg: '#FEF3C7' },
    medium: { color: '#3B82F6', bg: '#DBEAFE' },
    low: { color: '#6B7280', bg: '#F3F4F6' },
  };
  const pColor = priorityColors[priority] || priorityColors.medium;

  // Determine awaiting party
  const getAwaitingParty = () => {
    const status = job.status?.toLowerCase() || '';
    if (status.includes('awaiting quote') || status.includes('assigned')) return { party: 'Contractor', icon: Wrench };
    if (status.includes('review') || status.includes('approval')) return { party: 'Helpdesk', icon: User };
    if (status.includes('invoice')) return { party: 'Finance', icon: DollarSign };
    if (status.includes('scheduled') || status.includes('progress')) return { party: 'Contractor', icon: Wrench };
    return { party: '-', icon: Clock };
  };
  const awaiting = getAwaitingParty();
  const AwaitingIcon = awaiting.icon;

  return (
    <div 
      className="flex items-center justify-between px-4 py-2 rounded-lg mb-4"
      style={{ backgroundColor: sla.bgColor }}
      data-testid="sla-status-strip"
    >
      {/* SLA Status */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <div 
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: sla.color }}
          />
          <span className="text-xs font-bold" style={{ color: sla.color }}>
            {sla.status}
          </span>
          {sla.detail && (
            <span className="text-xs" style={{ color: sla.color }}>
              • {sla.detail}
            </span>
          )}
        </div>

        {/* SLA Target */}
        <div className="flex items-center gap-1.5 text-xs">
          <Clock className="w-3 h-3 text-slate-400" />
          <span className="text-slate-500">Target:</span>
          <span className="font-medium text-slate-700">
            {job.sla_due_at ? new Date(job.sla_due_at).toLocaleString('en-GB', { 
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
            }) : 'Not set'}
          </span>
        </div>
      </div>

      {/* Right side indicators */}
      <div className="flex items-center gap-4">
        {/* Priority */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">Priority:</span>
          <span 
            className="px-2 py-0.5 rounded text-xs font-semibold"
            style={{ backgroundColor: pColor.bg, color: pColor.color }}
          >
            {job.priority || 'Medium'}
          </span>
        </div>

        {/* Awaiting */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">Awaiting:</span>
          <div className="flex items-center gap-1 px-2 py-0.5 bg-white rounded text-xs font-medium text-slate-700">
            <AwaitingIcon className="w-3 h-3 text-slate-400" />
            {awaiting.party}
          </div>
        </div>

        {/* Owner */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">Owner:</span>
          <span className="text-xs font-medium text-slate-700">
            {job.assigned_to || 'Helpdesk'}
          </span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// QUOTE REVIEW DRAWER - Inline quote approval workflow
// =============================================================================
export function QuoteReviewDrawer({ 
  isOpen, 
  onClose, 
  job, 
  quote,
  onApprove,
  onReject,
  onRequestRevision,
  getAuthHeaders 
}) {
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [revisionNote, setRevisionNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  if (!isOpen || !quote) return null;

  // Calculate variance against benchmark
  const benchmark = job.benchmark_value || quote.total_inc_vat * 0.9; // Use benchmark or estimate
  const variance = quote.total_inc_vat - benchmark;
  const variancePercent = ((variance / benchmark) * 100).toFixed(1);
  const isOverBudget = variance > 0;

  // Breakdown analysis
  const labour = quote.labour_cost || 0;
  const materials = quote.materials_cost || 0;
  const other = quote.other_cost || 0;
  const subtotal = quote.total_ex_vat || labour + materials + other;
  const vat = quote.vat_amount || subtotal * 0.2;
  const total = quote.total_inc_vat || subtotal + vat;

  const handleApprove = async () => {
    setLoading(true);
    setAction('approve');
    try {
      if (onApprove) await onApprove();
      toast.success('Quote approved');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setLoading(false);
      setAction(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setLoading(true);
    setAction('reject');
    try {
      if (onReject) await onReject(rejectionReason);
      toast.success('Quote rejected');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject quote');
    } finally {
      setLoading(false);
      setAction(null);
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionNote.trim()) {
      toast.error('Please provide revision instructions');
      return;
    }
    setLoading(true);
    setAction('revision');
    try {
      if (onRequestRevision) await onRequestRevision(revisionNote);
      toast.success('Revision requested');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request revision');
    } finally {
      setLoading(false);
      setAction(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="quote-review-drawer">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Quote Review</h2>
              <p className="text-sm text-slate-500">
                {job.job_reference} • {job.contractor_name || 'Unknown Contractor'}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-200 rounded-lg transition"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Variance Alert */}
          {isOverBudget && Math.abs(variancePercent) > 10 && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  Quote {variancePercent}% over benchmark
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  This quote exceeds the expected benchmark by £{variance.toLocaleString()}. 
                  Consider requesting a breakdown or comparing with other contractors.
                </p>
              </div>
            </div>
          )}

          {/* Quote Summary */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Quote Summary</h3>
            </div>
            <div className="p-4 space-y-3">
              {/* Submitted by */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Submitted by</span>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center">
                    <Wrench className="w-3 h-3 text-teal-600" />
                  </div>
                  <span className="font-medium text-slate-700">{quote.submitted_by || job.contractor_name}</span>
                </div>
              </div>
              
              {/* Submitted date */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Submitted</span>
                <span className="text-slate-700">
                  {quote.submitted_at ? new Date(quote.submitted_at).toLocaleString() : 'N/A'}
                </span>
              </div>

              {/* Validity */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Valid until</span>
                <span className="text-slate-700">
                  {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : '30 days from submission'}
                </span>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Cost Breakdown</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Labour</span>
                <span className="font-medium text-slate-800">£{labour.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Materials</span>
                <span className="font-medium text-slate-800">£{materials.toLocaleString()}</span>
              </div>
              {other > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Other costs</span>
                  <span className="font-medium text-slate-800">£{other.toLocaleString()}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
                <span className="text-slate-600">Subtotal (ex VAT)</span>
                <span className="font-medium text-slate-800">£{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">VAT (20%)</span>
                <span className="font-medium text-slate-800">£{vat.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-200">
                <span className="font-semibold text-slate-800">Total (inc VAT)</span>
                <span className="text-lg font-bold text-teal-600">£{total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Benchmark Comparison */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Benchmark Comparison</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Benchmark value</span>
                <span className="font-medium text-slate-800">£{benchmark.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Quote value</span>
                <span className="font-medium text-slate-800">£{total.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
                <span className="text-slate-600">Variance</span>
                <div className="flex items-center gap-1">
                  {isOverBudget ? (
                    <TrendingUp className="w-4 h-4 text-red-500" />
                  ) : variance < 0 ? (
                    <TrendingDown className="w-4 h-4 text-green-500" />
                  ) : (
                    <Minus className="w-4 h-4 text-slate-400" />
                  )}
                  <span className={`font-semibold ${
                    isOverBudget ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-slate-600'
                  }`}>
                    {variance >= 0 ? '+' : ''}£{variance.toLocaleString()} ({variancePercent}%)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Contractor Notes */}
          {quote.notes && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">Contractor Notes</h3>
              </div>
              <div className="p-4">
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{quote.notes}</p>
              </div>
            </div>
          )}

          {/* Attachments */}
          {(quote.attachments?.length > 0 || quote.pdf_url) && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">Attachments</h3>
              </div>
              <div className="p-4 space-y-2">
                {quote.pdf_url && (
                  <a 
                    href={quote.pdf_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition"
                  >
                    <FileText className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium text-slate-700 flex-1">Quote Document (PDF)</span>
                    <Download className="w-4 h-4 text-slate-400" />
                  </a>
                )}
                {quote.attachments?.map((att, idx) => (
                  <a 
                    key={idx}
                    href={att.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition"
                  >
                    <File className="w-5 h-5 text-blue-500" />
                    <span className="text-sm font-medium text-slate-700 flex-1">{att.name || `Attachment ${idx + 1}`}</span>
                    <Download className="w-4 h-4 text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Rejection Reason Input */}
          {action === 'reject' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <label className="block text-sm font-medium text-red-800 mb-2">Rejection Reason *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this quote is being rejected..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-red-200 rounded-lg focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none resize-none"
              />
            </div>
          )}

          {/* Revision Request Input */}
          {action === 'revision' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <label className="block text-sm font-medium text-amber-800 mb-2">Revision Instructions *</label>
              <textarea
                value={revisionNote}
                onChange={(e) => setRevisionNote(e.target.value)}
                placeholder="What changes should the contractor make to the quote?"
                rows={3}
                className="w-full px-3 py-2 text-sm border border-amber-200 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
              />
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          {!action ? (
            <div className="flex gap-3">
              <button
                onClick={() => setAction('reject')}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-white border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50 transition disabled:opacity-50"
              >
                Reject
              </button>
              <button
                onClick={() => setAction('revision')}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-white border border-amber-300 text-amber-600 rounded-lg font-medium hover:bg-amber-50 transition disabled:opacity-50"
              >
                Request Revision
              </button>
              <button
                onClick={handleApprove}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && action === 'approve' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Approve Quote
              </button>
            </div>
          ) : action === 'reject' ? (
            <div className="flex gap-3">
              <button
                onClick={() => setAction(null)}
                className="flex-1 py-2.5 px-4 bg-white border border-slate-300 text-slate-600 rounded-lg font-medium hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                Confirm Rejection
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setAction(null)}
                className="flex-1 py-2.5 px-4 bg-white border border-slate-300 text-slate-600 rounded-lg font-medium hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestRevision}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                Send Revision Request
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// INVOICE REVIEW DRAWER - Inline invoice approval workflow
// =============================================================================
export function InvoiceReviewDrawer({ 
  isOpen, 
  onClose, 
  job, 
  invoice,
  onApprove,
  onQuery,
  onEscalate,
  getAuthHeaders 
}) {
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [queryNote, setQueryNote] = useState('');

  if (!isOpen || !invoice) return null;

  // Compare values
  const quote = job.quotes?.[0] || {};
  const poValue = job.po_value || quote.total_inc_vat;
  const invoiceValue = invoice.total || invoice.amount || 0;
  const variance = invoiceValue - poValue;
  const variancePercent = poValue ? ((variance / poValue) * 100).toFixed(1) : 0;

  // Validation checks
  const validations = [
    { 
      label: 'Purchase Order', 
      passed: !!job.po_number,
      detail: job.po_number || 'No PO issued',
      critical: true
    },
    { 
      label: 'Evidence uploaded', 
      passed: (job.evidence?.photos?.length > 0 || job.evidence?.documents?.length > 0),
      detail: `${job.evidence?.photos?.length || 0} photos, ${job.evidence?.documents?.length || 0} docs`,
      critical: true
    },
    { 
      label: 'SAT note present', 
      passed: !!job.sat_note,
      detail: job.sat_note ? 'Received' : 'Missing',
      critical: true
    },
    { 
      label: 'Job marked complete', 
      passed: ['Completed', 'Awaiting Invoice', 'Invoice Submitted', 'Invoiced'].includes(job.status),
      detail: job.status,
      critical: false
    },
    { 
      label: 'Invoice within PO value', 
      passed: variance <= 0 || Math.abs(variancePercent) <= 10,
      detail: variance > 0 ? `+${variancePercent}% over` : 'Within budget',
      critical: variancePercent > 10
    },
  ];

  const criticalIssues = validations.filter(v => !v.passed && v.critical);
  const hasBlockingIssues = criticalIssues.length > 0;

  const handleApprove = async () => {
    if (hasBlockingIssues) {
      toast.error('Cannot approve: Critical issues must be resolved');
      return;
    }
    setLoading(true);
    setAction('approve');
    try {
      if (onApprove) await onApprove();
      toast.success('Invoice approved for payment');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve invoice');
    } finally {
      setLoading(false);
      setAction(null);
    }
  };

  const handleQuery = async () => {
    if (!queryNote.trim()) {
      toast.error('Please provide details for the query');
      return;
    }
    setLoading(true);
    setAction('query');
    try {
      if (onQuery) await onQuery(queryNote);
      toast.success('Invoice query sent to contractor');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to query invoice');
    } finally {
      setLoading(false);
      setAction(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="invoice-review-drawer">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Invoice Review</h2>
              <p className="text-sm text-slate-500">
                {invoice.invoice_number || `INV-${job.id?.slice(0,6)}`} • {job.contractor_name}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg transition">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Critical Issues Alert */}
          {hasBlockingIssues && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">
                  {criticalIssues.length} blocking issue{criticalIssues.length > 1 ? 's' : ''} must be resolved
                </p>
                <ul className="text-xs text-red-600 mt-1 space-y-0.5">
                  {criticalIssues.map((issue, idx) => (
                    <li key={idx}>• {issue.label}: {issue.detail}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Invoice Summary */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Invoice Details</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Invoice Number</span>
                <span className="font-medium text-slate-700">{invoice.invoice_number || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Invoice Date</span>
                <span className="text-slate-700">
                  {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Payment Terms</span>
                <span className="text-slate-700">{invoice.payment_terms || '30 days'}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-200">
                <span className="font-semibold text-slate-800">Invoice Total</span>
                <span className="text-lg font-bold text-teal-600">
                  £{invoiceValue.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* PO vs Invoice Comparison */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Value Comparison</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Approved Quote</span>
                <span className="font-medium text-slate-800">£{(quote.total_inc_vat || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">PO Value</span>
                <span className="font-medium text-slate-800">£{(poValue || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Invoice Value</span>
                <span className="font-medium text-slate-800">£{invoiceValue.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
                <span className="text-slate-600">Variance</span>
                <div className="flex items-center gap-1">
                  {variance > 0 ? (
                    <TrendingUp className="w-4 h-4 text-red-500" />
                  ) : variance < 0 ? (
                    <TrendingDown className="w-4 h-4 text-green-500" />
                  ) : (
                    <Minus className="w-4 h-4 text-slate-400" />
                  )}
                  <span className={`font-semibold ${
                    variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-slate-600'
                  }`}>
                    {variance >= 0 ? '+' : ''}£{variance.toLocaleString()} ({variancePercent}%)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Validation Checklist */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Approval Checklist</h3>
            </div>
            <div className="p-4 space-y-2">
              {validations.map((v, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-2">
                    {v.passed ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : v.critical ? (
                      <XCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className="text-sm text-slate-700">{v.label}</span>
                  </div>
                  <span className={`text-xs font-medium ${
                    v.passed ? 'text-green-600' : v.critical ? 'text-red-600' : 'text-amber-600'
                  }`}>
                    {v.detail}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Invoice Attachments */}
          {invoice.attachment_url && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">Invoice Document</h3>
              </div>
              <div className="p-4">
                <a 
                  href={invoice.attachment_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition"
                >
                  <Receipt className="w-5 h-5 text-teal-500" />
                  <span className="text-sm font-medium text-slate-700 flex-1">View Invoice PDF</span>
                  <Eye className="w-4 h-4 text-slate-400" />
                </a>
              </div>
            </div>
          )}

          {/* Query Input */}
          {action === 'query' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <label className="block text-sm font-medium text-amber-800 mb-2">Query Details *</label>
              <textarea
                value={queryNote}
                onChange={(e) => setQueryNote(e.target.value)}
                placeholder="Describe what needs clarification or correction..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-amber-200 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
              />
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          {!action ? (
            <div className="flex gap-3">
              <button
                onClick={() => setAction('query')}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-white border border-amber-300 text-amber-600 rounded-lg font-medium hover:bg-amber-50 transition disabled:opacity-50"
              >
                Query Invoice
              </button>
              <button
                onClick={handleApprove}
                disabled={loading || hasBlockingIssues}
                className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition disabled:opacity-50 flex items-center justify-center gap-2 ${
                  hasBlockingIssues 
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                {loading && action === 'approve' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Approve for Payment
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setAction(null)}
                className="flex-1 py-2.5 px-4 bg-white border border-slate-300 text-slate-600 rounded-lg font-medium hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleQuery}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                Send Query
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ENHANCED EVIDENCE PANEL - Drag-drop with categorization
// =============================================================================
export function EnhancedEvidencePanel({ job, onUpload, onRefresh, getAuthHeaders }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('photo_after');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const evidence = job.evidence || { photos: [], documents: [] };
  const photos = evidence.photos || [];
  const documents = evidence.documents || [];
  
  // Job status checks for operational rules
  const jobStatus = job.status?.toLowerCase() || '';
  const isCompleted = ['completed', 'completed awaiting review'].some(s => jobStatus.includes(s));
  const isAwaitingInvoice = jobStatus.includes('awaiting invoice') || jobStatus.includes('invoice');
  const isPaid = jobStatus.includes('paid');

  // =============================================================================
  // EVIDENCE CATEGORIES - Full operational categorization
  // =============================================================================
  const EVIDENCE_CATEGORIES = [
    { key: 'photo_before', label: 'Before Photos', icon: Camera, color: '#F59E0B', required: true },
    { key: 'photo_during', label: 'During Photos', icon: Camera, color: '#3B82F6', required: false },
    { key: 'photo_after', label: 'After Photos', icon: Camera, color: '#22C55E', required: true },
    { key: 'satnote', label: 'SAT Note', icon: CheckCircle, color: '#0D9488', required: true },
    { key: 'certificate', label: 'Certificate', icon: Shield, color: '#6366F1', required: false },
    { key: 'jobsheet', label: 'Engineer Job Sheet', icon: FileText, color: '#EC4899', required: false },
    { key: 'invoice', label: 'Invoice', icon: Receipt, color: '#F97316', required: true },
    { key: 'other', label: 'Other Documents', icon: File, color: '#6B7280', required: false },
  ];

  // Categorize existing evidence
  const categorizedEvidence = useMemo(() => {
    const cats = {};
    EVIDENCE_CATEGORIES.forEach(cat => {
      cats[cat.key] = { items: [], count: 0 };
    });
    
    // Categorize photos
    photos.forEach(p => {
      const stage = p.stage?.toLowerCase() || 'after';
      if (stage === 'before') cats.photo_before.items.push(p);
      else if (stage === 'during') cats.photo_during.items.push(p);
      else cats.photo_after.items.push(p);
    });
    
    // Categorize documents
    documents.forEach(d => {
      const type = d.type?.toLowerCase() || 'other';
      if (type === 'satnote' || type === 'sat_note') cats.satnote.items.push(d);
      else if (type === 'certificate') cats.certificate.items.push(d);
      else if (type === 'jobsheet' || type === 'job_sheet') cats.jobsheet.items.push(d);
      else if (type === 'invoice') cats.invoice.items.push(d);
      else cats.other.items.push(d);
    });
    
    // Update counts
    Object.keys(cats).forEach(k => { cats[k].count = cats[k].items.length; });
    
    return cats;
  }, [photos, documents]);

  // =============================================================================
  // COMPLETENESS & OPERATIONAL BLOCKERS
  // =============================================================================
  const completeness = useMemo(() => {
    const checks = [
      { 
        key: 'photo_before', 
        label: 'Before Photos', 
        has: categorizedEvidence.photo_before.count > 0,
        required: true,
        blocksCompletion: true
      },
      { 
        key: 'photo_after', 
        label: 'After Photos', 
        has: categorizedEvidence.photo_after.count > 0,
        required: true,
        blocksCompletion: true
      },
      { 
        key: 'satnote', 
        label: 'SAT Note', 
        has: categorizedEvidence.satnote.count > 0 || !!job.sat_note,
        required: true,
        blocksInvoice: true
      },
      { 
        key: 'jobsheet', 
        label: 'Job Sheet', 
        has: categorizedEvidence.jobsheet.count > 0,
        required: false
      },
      { 
        key: 'invoice', 
        label: 'Invoice', 
        has: categorizedEvidence.invoice.count > 0,
        required: true,
        blocksPayment: true
      },
    ];
    
    const requiredChecks = checks.filter(c => c.required);
    const passedRequired = requiredChecks.filter(c => c.has).length;
    const percent = Math.round((passedRequired / requiredChecks.length) * 100);
    
    // Determine operational blockers
    const canComplete = checks.filter(c => c.blocksCompletion && !c.has).length === 0;
    const canInvoice = checks.filter(c => c.blocksInvoice && !c.has).length === 0;
    const canApprovePayment = checks.filter(c => c.blocksPayment && !c.has).length === 0;
    
    const missingForCompletion = checks.filter(c => c.blocksCompletion && !c.has).map(c => c.label);
    const missingForInvoice = checks.filter(c => c.blocksInvoice && !c.has).map(c => c.label);
    const missingForPayment = checks.filter(c => c.blocksPayment && !c.has).map(c => c.label);
    
    return { 
      checks, 
      percent, 
      passed: passedRequired, 
      total: requiredChecks.length,
      canComplete,
      canInvoice,
      canApprovePayment,
      missingForCompletion,
      missingForInvoice,
      missingForPayment
    };
  }, [categorizedEvidence, job.sat_note]);

  // =============================================================================
  // DRAG & DROP HANDLERS
  // =============================================================================
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = [...e.dataTransfer.files];
    if (files.length > 0) {
      setShowUploadModal(true);
    }
  };

  const handleFileSelect = async (e) => {
    const files = [...e.target.files];
    if (files.length > 0) {
      await handleUpload(files, uploadCategory);
    }
  };

  const handleUpload = async (files, category = 'photo_after') => {
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('category', category);
      
      // Determine stage for photos
      if (category.startsWith('photo_')) {
        formData.append('stage', category.replace('photo_', ''));
      } else {
        formData.append('document_type', category);
      }
      
      await axios.post(
        `${API}/api/fm/jobs/${job.id}/upload-evidence`,
        formData,
        { 
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          withCredentials: true 
        }
      );
      
      toast.success(`${files.length} file(s) uploaded as ${EVIDENCE_CATEGORIES.find(c => c.key === category)?.label || category}`);
      if (onRefresh) onRefresh();
      setShowUploadModal(false);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  // Filter items based on active category
  const getFilteredItems = () => {
    if (activeCategory === 'all') {
      return { 
        photos: [...categorizedEvidence.photo_before.items, ...categorizedEvidence.photo_during.items, ...categorizedEvidence.photo_after.items],
        documents: [...categorizedEvidence.satnote.items, ...categorizedEvidence.certificate.items, ...categorizedEvidence.jobsheet.items, ...categorizedEvidence.invoice.items, ...categorizedEvidence.other.items]
      };
    }
    if (activeCategory.startsWith('photo_')) {
      return { photos: categorizedEvidence[activeCategory]?.items || [], documents: [] };
    }
    return { photos: [], documents: categorizedEvidence[activeCategory]?.items || [] };
  };

  const filtered = getFilteredItems();
  const totalCount = photos.length + documents.length;

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="enhanced-evidence-panel">
      {/* =================================================================== */}
      {/* OPERATIONAL BLOCKERS - Warning Banners */}
      {/* =================================================================== */}
      {isCompleted && !completeness.canComplete && (
        <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Cannot Mark Complete</p>
            <p className="text-xs text-red-600 mt-0.5">
              Missing: {completeness.missingForCompletion.join(', ')}
            </p>
          </div>
        </div>
      )}
      
      {isAwaitingInvoice && !completeness.canInvoice && (
        <div className="m-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Cannot Submit Invoice</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Missing: {completeness.missingForInvoice.join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* HEADER with Completeness Indicator */}
      {/* =================================================================== */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-800">Evidence & Documents</h3>
            <p className="text-xs text-slate-500 mt-0.5">{totalCount} items uploaded</p>
          </div>
          
          {/* Completeness Badge */}
          <div className="flex items-center gap-3">
            <div className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
              completeness.percent === 100 
                ? 'bg-green-100 text-green-700' 
                : completeness.percent >= 50 
                ? 'bg-amber-100 text-amber-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {completeness.percent === 100 ? 'Audit Ready' : `${completeness.percent}% Complete`}
            </div>
          </div>
        </div>

        {/* Completeness Checklist */}
        <div className="grid grid-cols-5 gap-2 mb-4">
          {completeness.checks.map(check => (
            <div 
              key={check.key}
              className={`p-2 rounded-lg border text-center ${
                check.has 
                  ? 'bg-green-50 border-green-200' 
                  : check.required 
                  ? 'bg-red-50 border-red-200'
                  : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className={`w-5 h-5 mx-auto mb-1 rounded-full flex items-center justify-center ${
                check.has ? 'bg-green-500' : check.required ? 'bg-red-400' : 'bg-slate-300'
              }`}>
                {check.has ? (
                  <Check className="w-3 h-3 text-white" />
                ) : (
                  <X className="w-3 h-3 text-white" />
                )}
              </div>
              <p className={`text-[10px] font-medium ${
                check.has ? 'text-green-700' : check.required ? 'text-red-700' : 'text-slate-500'
              }`}>
                {check.label}
              </p>
              {check.required && !check.has && (
                <span className="text-[9px] text-red-500">Required</span>
              )}
            </div>
          ))}
        </div>

        {/* Category Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          <button
            onClick={() => setActiveCategory('all')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition ${
              activeCategory === 'all' 
                ? 'bg-teal-50 text-teal-700 font-medium' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            All ({totalCount})
          </button>
          {EVIDENCE_CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const count = categorizedEvidence[cat.key]?.count || 0;
            if (count === 0 && !cat.required) return null;
            
            return (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition ${
                  activeCategory === cat.key 
                    ? 'bg-teal-50 text-teal-700 font-medium' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-3 h-3" style={{ color: cat.color }} />
                {cat.label}
                <span className={`text-[10px] ${count === 0 && cat.required ? 'text-red-500 font-bold' : 'opacity-70'}`}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* =================================================================== */}
      {/* UPLOAD AREA - Drag & Drop */}
      {/* =================================================================== */}
      <div 
        className={`m-4 border-2 border-dashed rounded-xl p-6 transition ${
          dragActive ? 'border-teal-400 bg-teal-50' : 'border-slate-200 hover:border-slate-300'
        }`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <div className="text-center">
          {uploading ? (
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-2" />
          ) : (
            <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          )}
          <p className="text-sm text-slate-600 font-medium mb-3">
            {dragActive ? 'Drop files here' : 'Drag & drop files or select category'}
          </p>
          
          {/* Quick Upload Buttons by Category */}
          <div className="flex flex-wrap justify-center gap-2">
            {EVIDENCE_CATEGORIES.slice(0, 4).map(cat => {
              const Icon = cat.icon;
              return (
                <label
                  key={cat.key}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50 cursor-pointer transition"
                  style={{ borderColor: categorizedEvidence[cat.key].count === 0 && cat.required ? '#FCA5A5' : undefined }}
                >
                  <Icon className="w-3 h-3" style={{ color: cat.color }} />
                  {cat.label}
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    className="hidden"
                    onChange={(e) => {
                      const files = [...e.target.files];
                      if (files.length > 0) handleUpload(files, cat.key);
                    }}
                  />
                </label>
              );
            })}
          </div>
          
          {/* More upload options */}
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            {EVIDENCE_CATEGORIES.slice(4).map(cat => {
              const Icon = cat.icon;
              return (
                <label
                  key={cat.key}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50 cursor-pointer transition"
                >
                  <Icon className="w-3 h-3" style={{ color: cat.color }} />
                  {cat.label}
                  <input
                    type="file"
                    multiple
                    accept={cat.key === 'invoice' ? '.pdf,.doc,.docx' : 'image/*,.pdf,.doc,.docx'}
                    className="hidden"
                    onChange={(e) => {
                      const files = [...e.target.files];
                      if (files.length > 0) handleUpload(files, cat.key);
                    }}
                  />
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* PHOTO GRID - Grouped by Stage */}
      {/* =================================================================== */}
      {filtered.photos.length > 0 && (
        <div className="px-4 pb-4">
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            <Camera className="w-3 h-3" />
            Photos ({filtered.photos.length})
          </p>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {filtered.photos.map((photo, idx) => (
              <div 
                key={idx} 
                className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group cursor-pointer"
                onClick={() => setPreviewImage(photo)}
              >
                <img src={photo.url} alt="" className="w-full h-full object-cover" />
                {photo.stage && (
                  <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] text-white font-medium ${
                    photo.stage === 'before' ? 'bg-amber-500' :
                    photo.stage === 'during' ? 'bg-blue-500' : 'bg-green-500'
                  }`}>
                    {photo.stage}
                  </div>
                )}
                <div className="absolute bottom-1 right-1 text-[9px] text-white bg-black/50 px-1 rounded">
                  {photo.uploaded_at ? new Date(photo.uploaded_at).toLocaleDateString() : ''}
                </div>
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* DOCUMENTS LIST */}
      {/* =================================================================== */}
      {filtered.documents.length > 0 && (
        <div className="px-4 pb-4">
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            Documents ({filtered.documents.length})
          </p>
          <div className="space-y-2">
            {filtered.documents.map((doc, idx) => {
              const catInfo = EVIDENCE_CATEGORIES.find(c => c.key === doc.type) || { icon: File, color: '#6B7280' };
              const DocIcon = catInfo.icon;
              
              return (
                <a 
                  key={idx}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition"
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${catInfo.color}20` }}
                  >
                    <DocIcon className="w-4 h-4" style={{ color: catInfo.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{doc.name || `Document ${idx + 1}`}</p>
                    <p className="text-[10px] text-slate-400">
                      {doc.uploaded_by && `${doc.uploaded_by} • `}
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : ''}
                    </p>
                  </div>
                  <Download className="w-4 h-4 text-slate-400" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {filtered.photos.length === 0 && filtered.documents.length === 0 && (
        <div className="px-4 pb-4 text-center py-6">
          <Image className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">
            {activeCategory === 'all' ? 'No evidence uploaded yet' : `No ${EVIDENCE_CATEGORIES.find(c => c.key === activeCategory)?.label || activeCategory} found`}
          </p>
          {activeCategory !== 'all' && EVIDENCE_CATEGORIES.find(c => c.key === activeCategory)?.required && (
            <p className="text-xs text-red-500 mt-1">This is required for job completion</p>
          )}
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button 
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-4xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img 
              src={previewImage.url} 
              alt="" 
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center text-white">
              {previewImage.stage && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold mr-2 ${
                  previewImage.stage === 'before' ? 'bg-amber-500' :
                  previewImage.stage === 'during' ? 'bg-blue-500' : 'bg-green-500'
                }`}>
                  {previewImage.stage.toUpperCase()}
                </span>
              )}
              <span className="text-sm text-white/70">
                {previewImage.uploaded_by && `Uploaded by ${previewImage.uploaded_by}`}
                {previewImage.uploaded_at && ` on ${new Date(previewImage.uploaded_at).toLocaleString()}`}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// UNIFIED ACTIVITY FEED - All events in chronological order
// =============================================================================
export function UnifiedActivityFeed({ job, communications = [] }) {
  const [filter, setFilter] = useState('all');

  // Build unified timeline
  const timeline = useMemo(() => {
    const events = [];
    
    // Add job creation
    events.push({
      type: 'system',
      action: 'Job Created',
      detail: `${job.category || 'General'} job logged`,
      timestamp: job.created_at,
      by: job.created_by || 'System',
      icon: Plus,
      color: '#0D9488'
    });

    // Add status changes from history
    (job.history || []).forEach(h => {
      events.push({
        type: 'system',
        action: h.action,
        detail: h.details,
        timestamp: h.timestamp,
        by: h.by || 'System',
        icon: History,
        color: '#6366F1'
      });
    });

    // Add communications
    communications.forEach(c => {
      events.push({
        type: c.type || 'internal',
        action: c.type === 'contractor' ? 'Contractor Message' : 
                c.type === 'resident' ? 'Resident Contact' : 'Internal Note',
        detail: c.message || c.content || c.text,
        timestamp: c.created_at || c.timestamp,
        by: c.by || c.author || 'Unknown',
        icon: c.type === 'contractor' ? Wrench : 
              c.type === 'resident' ? User : MessageSquare,
        color: c.type === 'contractor' ? '#F97316' : 
               c.type === 'resident' ? '#22C55E' : '#3B82F6'
      });
    });

    // Add quote events
    (job.quotes || []).forEach(q => {
      events.push({
        type: 'quote',
        action: 'Quote Submitted',
        detail: `£${q.total_inc_vat?.toLocaleString()} by ${q.submitted_by || job.contractor_name}`,
        timestamp: q.submitted_at,
        by: q.submitted_by || job.contractor_name,
        icon: FileText,
        color: '#EAB308'
      });
      if (q.approved_at) {
        events.push({
          type: 'approval',
          action: 'Quote Approved',
          detail: `£${q.total_inc_vat?.toLocaleString()} approved`,
          timestamp: q.approved_at,
          by: q.approved_by || 'Helpdesk',
          icon: CheckCircle,
          color: '#22C55E'
        });
      }
    });

    // Sort by timestamp (newest first)
    events.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    
    return events;
  }, [job, communications]);

  const filters = [
    { key: 'all', label: 'All Activity' },
    { key: 'system', label: 'System' },
    { key: 'internal', label: 'Notes' },
    { key: 'contractor', label: 'Contractor' },
    { key: 'approval', label: 'Approvals' },
  ];

  const filteredEvents = filter === 'all' ? timeline : timeline.filter(e => e.type === filter);

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="unified-activity-feed">
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Activity Feed</h3>
          <span className="text-xs text-slate-500">{timeline.length} events</span>
        </div>
        
        {/* Filters */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition ${
                filter === f.key 
                  ? 'bg-teal-50 text-teal-700 font-medium' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="max-h-[500px] overflow-y-auto">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-7 top-0 bottom-0 w-px bg-slate-100" />
          
          {filteredEvents.map((event, idx) => {
            const Icon = event.icon;
            return (
              <div 
                key={idx} 
                className="relative flex gap-3 px-4 py-3 hover:bg-slate-50 transition"
              >
                {/* Icon */}
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                  style={{ backgroundColor: `${event.color}20` }}
                >
                  <Icon className="w-3 h-3" style={{ color: event.color }} />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-slate-800">{event.action}</p>
                    <span className="text-[10px] text-slate-400 whitespace-nowrap">
                      {formatTimestamp(event.timestamp)}
                    </span>
                  </div>
                  {event.detail && (
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{event.detail}</p>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1">by {event.by}</p>
                </div>
              </div>
            );
          })}

          {filteredEvents.length === 0 && (
            <div className="p-8 text-center">
              <History className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No activity to show</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default { 
  SLAStatusStrip, 
  QuoteReviewDrawer, 
  InvoiceReviewDrawer, 
  EnhancedEvidencePanel, 
  UnifiedActivityFeed 
};
